import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CircularProgress,
  Link,
  Select,
  Option,
  Button,
  Stack,
  Alert,
  Input,
  IconButton,
} from '@mui/joy';
import { Link as RouterLink } from 'react-router-dom';
import { ResponsiveTable } from '../common/ResponsiveTable';
import { Plus, Trash, Copy, ClipboardText as ClipboardCheck } from '@phosphor-icons/react';
import { PITCH_MULTIPLIERS } from './lineDrawingConstants';
import type { DetailedMeasurement } from '../../hooks/measurements/useDetailedMeasurements';
import { useTakeoffItems } from '../../hooks/measurements/useTakeoffItems';
import { TakeoffOrderDialog } from './TakeoffOrderDialog';
import { AddTakeoffItemDialog } from './AddTakeoffItemDialog';
import { useMaterialTakeoff, type TakeoffRow } from '../../hooks/measurements/useMaterialTakeoff';
import { useAuth } from '../../contexts/AuthContext';
import {
  MATERIAL_TYPES,
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  computeTakeoffQuantity,
  describeTakeoffFormula,
  type TakeoffDrivers,
} from './takeoffConstants';

interface MaterialTakeoffPanelProps {
  measurement: {
    id: number;
    address: string;
    customer_name?: string;
    created_at: string;
  };
  detailedMeasurement: DetailedMeasurement | null;
}

export const MaterialTakeoffPanel: React.FC<MaterialTakeoffPanelProps> = (props) => (
  <MaterialTakeoffEditor key={props.detailedMeasurement?.id ?? 'none'} {...props} />
);

const MaterialTakeoffEditor: React.FC<MaterialTakeoffPanelProps> = ({ measurement, detailedMeasurement }) => {
  const [selectedMaterial, setSelectedMaterial] = useState<string>('architectural');
  const [copied, setCopied] = useState(false);
  const { user } = useAuth();
  const saved = useMaterialTakeoff(detailedMeasurement?.id, selectedMaterial);
  const [draft, setDraft] = useState<{ rows: TakeoffRow[]; revision: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const [orderRevision, setOrderRevision] = useState<number | null>(null);
  useEffect(() => {
    if (!draft) return;
    const warn = (event: BeforeUnloadEvent) => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [draft]);
  const { data: catalog, isLoading: catalogLoading, error: catalogError } = useTakeoffItems();

  // Measurement figures the catalog formulas draw from. Hips, valleys and
  // rakes are slope-adjusted by the pitch multiplier; ridges and eaves are not.
  const { drivers, wastePct } = useMemo(() => {
    if (!detailedMeasurement) {
      return { drivers: null as TakeoffDrivers | null, wastePct: 15 };
    }
    const dm = detailedMeasurement;
    const flatArea = Number(dm.total_area_sqft) || 0;
    const pitch = dm.predominant_pitch || '4/12';
    const multiplier = PITCH_MULTIPLIERS[pitch] || 1;
    const trueArea = flatArea * multiplier;

    const ridgeFt = Number(dm.total_ridges_ft) || 0;
    const hipFt = (Number(dm.total_hips_ft) || 0) * multiplier;
    const valleyFt = (Number(dm.total_valleys_ft) || 0) * multiplier;
    const eaveFt = Number(dm.total_eaves_ft) || 0;
    const rakeFt = (Number(dm.total_rakes_ft) || 0) * multiplier;
    const polygonPerimeterFt = Number((dm.raw_parsed_data as Record<string, unknown>)?.polygon_perimeter_ft) || 0;
    const dripEdgeFt = polygonPerimeterFt ? (polygonPerimeterFt + rakeFt) : (eaveFt + rakeFt);

    return {
      drivers: {
        squares: trueArea / 100,
        true_area_sqft: trueArea,
        ridge_lf: ridgeFt,
        hip_lf: hipFt,
        valley_lf: valleyFt,
        eave_lf: eaveFt,
        rake_lf: rakeFt,
        eave_plus_rake_lf: dripEdgeFt,
        valleys_plus_eaves_lf: valleyFt + eaveFt,
        fixed: 1,
      } satisfies TakeoffDrivers,
      wastePct: Math.round(Number(dm.waste_factor) || 15),
    };
  }, [detailedMeasurement]);

  const generatedRows = useMemo((): TakeoffRow[] => {
    if (!drivers || !catalog) return [];
    const wasteFraction = wastePct / 100;

    return CATEGORY_ORDER.flatMap((category) =>
      catalog
        .filter((item) =>
          item.category === category &&
          item.is_active &&
          (item.applies_to.length === 0 || item.applies_to.includes(selectedMaterial))
        )
        .sort((a, b) => a.sort_order - b.sort_order || a.id - b.id)
        .map((item) => ({
          id: `catalog-${item.id}`,
          category: CATEGORY_LABELS[item.category],
          material: item.name,
          quantity: String(computeTakeoffQuantity(item, drivers, wasteFraction)),
          unit: item.unit_label,
          notes: describeTakeoffFormula(item, drivers, wastePct),
        }))
        .filter((row) => Number(row.quantity) > 0)
    );
  }, [catalog, drivers, selectedMaterial, wastePct]);

  const takeoffRows = draft?.rows ?? saved.data?.rows ?? generatedRows;
  const canEdit = saved.isSuccess && (!!saved.data || !!catalog) && !saved.save.isPending;
  const invalidQuantity = takeoffRows.some(row => !/^\d+(\.\d{1,3})?$/.test(row.quantity) || Number(row.quantity) > 999999999.999);
  const updateRows = (rows: TakeoffRow[]) => {
    saved.save.reset();
    setDraft(previous => ({ rows, revision: previous?.revision ?? saved.data?.revision ?? 0 }));
    setCopied(false);
  };
  const saveChanges = async () => {
    if (!draft || invalidQuantity) return;
    try { await saved.save.mutateAsync(draft); setDraft(null); } catch { /* Keep the user's draft for retry. */ }
  };

  const createOrder = async () => {
    if (!canEdit || invalidQuantity || !takeoffRows.some(r => Number(r.quantity) > 0)) return;
    try {
      const snapshot = draft || !saved.data
        ? await saved.save.mutateAsync({ rows: takeoffRows, revision: draft?.revision ?? saved.data?.revision ?? 0 })
        : saved.data;
      setDraft(null); setOrderRevision(snapshot.revision);
    } catch { /* Save errors are shown below; never hand off unsaved quantities. */ }
  };
  const orderButton = <Button size="lg" disabled={!canEdit || invalidQuantity || !takeoffRows.some(r => Number(r.quantity) > 0)} loading={saved.save.isPending} onClick={createOrder}>Create Materials Order</Button>;

  const selectedMaterialLabel =
    MATERIAL_TYPES.find((m) => m.value === selectedMaterial)?.label || selectedMaterial;

  const handleCopyToClipboard = () => {
    if (!takeoffRows.length || invalidQuantity) return;

    const dm = detailedMeasurement;
    const pitch = dm?.predominant_pitch || 'N/A';
    const squares = dm ? Math.ceil((Number(dm.total_area_sqft) || 0) * (PITCH_MULTIPLIERS[pitch] || 1) * (1 + (Number(dm.waste_factor) || 15) / 100) / 100) : 0;

    let text = `MATERIAL TAKEOFF\n`;
    text += `================\n`;
    text += `Property: ${measurement.address}\n`;
    text += `Date: ${new Date(measurement.created_at).toLocaleDateString()}\n`;
    text += `Pitch: ${pitch} • Squares: ${squares}\n`;
    text += `Material: ${selectedMaterialLabel}\n\n`;

    let currentCategory = '';
    for (const item of takeoffRows) {
      if (item.category !== currentCategory) {
        currentCategory = item.category;
        text += `\n${currentCategory.toUpperCase()}\n`;
        text += `${'─'.repeat(50)}\n`;
      }
      text += `${item.material.padEnd(30)} ${String(item.quantity).padStart(5)} ${item.unit}\n`;
    }

    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!detailedMeasurement) {
    return (
      <Alert color="warning">
        No measurement data available. Draw roof sections and lines, then click "Save & Continue" to generate measurement data.
      </Alert>
    );
  }

  const dm = detailedMeasurement;
  const flatArea = Number(dm.total_area_sqft) || 0;
  const pitch = dm.predominant_pitch || '4/12';
  const multiplier = PITCH_MULTIPLIERS[pitch] || 1;
  const wasteFactor = Number(dm.waste_factor) || 15;
  const trueArea = flatArea * multiplier;
  const areaWithWaste = trueArea * (1 + wasteFactor / 100);
  const squares = Math.ceil(areaWithWaste / 100);

  // Group rows by category
  const categories = [...new Set(takeoffRows.map((item) => item.category))];

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ justifyContent: 'space-between', alignItems: { xs: 'stretch', sm: 'flex-start' } }}
      >
        <Box>
          <Typography level="h4">Material Takeoff</Typography>
          <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
            Edit your materials, then match products and prepare a distributor order.
          </Typography>
          {user?.is_organization_admin && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
              Customize these calculations in{' '}
              <Link component={RouterLink} to="/settings/takeoff" level="body-xs">
                Settings → Material Takeoff
              </Link>
              .
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
          {orderButton}
          <Button size="sm" startDecorator={<Plus size={16} />} disabled={!canEdit} onClick={() => setAdding(true)}>Add item</Button>
          <Button
            variant="outlined"
            color="primary"
            size="sm"
            startDecorator={copied ? <ClipboardCheck size={16} /> : <Copy size={16} />}
            disabled={!canEdit || invalidQuantity || !takeoffRows.length}
            onClick={handleCopyToClipboard}
          >
            {copied ? 'Copied!' : 'Copy to Clipboard'}
          </Button>
        </Stack>
      </Stack>

      {/* Job Summary — address column + four stats spread across the card */}
      <Card variant="soft" sx={{ backgroundColor: 'background.level1' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)', md: 'minmax(260px, 2.5fr) repeat(4, 1fr)' },
            columnGap: 3,
            rowGap: 2,
            alignItems: 'start',
          }}
        >
          <Box sx={{ gridColumn: { xs: '1 / -1', md: 'auto' }, minWidth: 0 }}>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'text.tertiary' }}>
              Property
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'bold' }}>
              {measurement.address}
            </Typography>
          </Box>
          <Box>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'text.tertiary' }}>
              Pitch
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'bold' }}>{pitch}</Typography>
          </Box>
          <Box>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'text.tertiary' }}>
              True Area
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'bold' }}>
              {Math.round(trueArea).toLocaleString()} sf
            </Typography>
          </Box>
          <Box>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'text.tertiary' }}>
              Squares (w/ waste)
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'bold', color: 'success.600' }}>
              {squares}
            </Typography>
          </Box>
          <Box>
            <Typography level="body-xs" sx={{ textTransform: 'uppercase', fontWeight: 'bold', color: 'text.tertiary' }}>
              Waste
            </Typography>
            <Typography level="body-md" sx={{ fontWeight: 'bold' }}>{wasteFactor}%</Typography>
          </Box>
        </Box>
      </Card>

      {/* Material Type Selector */}
      <Box sx={{ display: 'flex', alignItems: { xs: 'stretch', sm: 'center' }, gap: { xs: 1, sm: 2 }, flexDirection: { xs: 'column', sm: 'row' } }}>
        <Typography level="body-sm" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
          Roofing Material:
        </Typography>
        <Select
          disabled={!!draft || saved.save.isPending}
          value={selectedMaterial}
          onChange={(_, v) => v && setSelectedMaterial(v)}
          size="sm"
          sx={{ minWidth: { xs: 0, sm: 250 }, flex: { xs: 1, sm: 'none' } }}
        >
          {MATERIAL_TYPES.map((m) => (
            <Option key={m.value} value={m.value}>
              {m.label}
            </Option>
          ))}
        </Select>
      </Box>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' } }}>
        <Typography level="body-sm" sx={{ flex: 1 }}>
          {draft ? 'Unsaved changes — save or discard before changing roofing material.' : saved.data ? 'Saved takeoff. Quantities stay as saved until you edit or recalculate.' : 'Calculated from your measurements. Edit quantities or add and remove items, then save.'}
        </Typography>
        <Button size="sm" disabled={!canEdit || !draft || invalidQuantity} loading={saved.save.isPending} onClick={saveChanges}>Save changes</Button>
        {draft && <Button size="sm" variant="plain" color="neutral" disabled={saved.save.isPending} onClick={() => { setDraft(null); saved.save.reset(); }}>Discard changes</Button>}
        <Button size="sm" variant="outlined" color="neutral" disabled={!canEdit || !catalog || !!catalogError} onClick={() => {
          if (window.confirm('Replace this takeoff with freshly calculated quantities? You can review the result before saving.')) updateRows(generatedRows);
        }}>Recalculate</Button>
      </Stack>
      {saved.save.isSuccess && !draft && <Alert color="success">Takeoff saved.</Alert>}
      {saved.save.isError && <Alert color="danger">{(saved.save.error as { response?: { status?: number } })?.response?.status === 409 ? 'Someone else updated this takeoff. Copy your changes before reloading to review their version.' : 'Unable to save the takeoff. Your changes are still here; please try again.'}</Alert>}
      {invalidQuantity && <Alert color="warning">Enter a quantity of 0 or more, with up to 3 decimal places (maximum 999,999,999.999).</Alert>}
      {saved.isError && <Alert color="danger">Unable to load the saved takeoff. <Button size="sm" onClick={() => saved.refetch()}>Retry</Button></Alert>}
      {orderRevision !== null && <TakeoffOrderDialog measurementId={detailedMeasurement.id} materialType={selectedMaterial} sourceRevision={orderRevision} onClose={() => setOrderRevision(null)} />}
      {adding && <AddTakeoffItemDialog onClose={() => setAdding(false)} onAdd={row => { updateRows([...takeoffRows, row]); setAdding(false); }} />}
      {(catalogLoading || saved.isPending) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size="sm" />
        </Box>
      )}
      {!!catalogError && !catalogLoading && (
        <Alert color="danger">
          Failed to load your organization's takeoff settings. Refresh the page to try again.
        </Alert>
      )}

      {/* Takeoff Tables by Category */}
      {canEdit && !takeoffRows.length && <Alert color="neutral">This takeoff has no items. Add an item or recalculate to start again.</Alert>}
      {!saved.isError && !saved.isPending && categories.map((category) => {
        const categoryRows = takeoffRows.filter((item) => item.category === category);
        return (
          <Card key={category} variant="outlined">
            <Typography
              level="title-sm"
              sx={{
                textTransform: 'uppercase',
                fontSize: '11px',
                fontWeight: 'bold',
                letterSpacing: '0.5px',
                color: 'warning.500',
                mb: 1,
              }}
            >
              {category}
            </Typography>
            <ResponsiveTable<typeof categoryRows[number]>
              rows={categoryRows}
              getRowKey={(row) => row.id}
              columns={[
                {
                  key: 'material',
                  sortValue: (row) => row.material,
                  header: 'Material',
                  mobileVariant: 'header',
                  render: (item) => (
                    <Typography level="title-sm" sx={{ fontWeight: 600 }}>
                      {item.material}
                    </Typography>
                  ),
                },
                {
                  key: 'qty',
                  sortValue: (row) => Number(row.quantity),
                  header: 'Qty',
                  render: (item) => (
                    <Input
                      type="number" size="sm" value={item.quantity} disabled={!canEdit}
                      slotProps={{ input: { 'aria-label': `Quantity for ${item.material}`, min: 0, max: 999999999.999, step: 0.001 } }}
                      sx={{ width: 120, maxWidth: '100%' }}
                      onChange={event => updateRows(takeoffRows.map(row => row.id === item.id ? { ...row, quantity: event.target.value, notes: row.id.startsWith('catalog-') ? 'Quantity manually adjusted' : row.notes } : row))}
                    />
                  ),
                },
                {
                  key: 'unit',
                  sortValue: (row) => row.unit,
                  header: 'Unit',
                  render: (item) => (
                    <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                      {item.unit}
                    </Typography>
                  ),
                },
                {
                  key: 'notes',
                  header: 'Notes',
                  mobileVariant: 'body',
                  render: (item) => (
                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                      {item.notes}
                    </Typography>
                  ),
                },
                {
                  key: 'actions', header: 'Remove', mobileVariant: 'default',
                  render: item => <IconButton size="sm" variant="plain" color="danger" aria-label={`Remove ${item.material}`} disabled={!canEdit} onClick={() => updateRows(takeoffRows.filter(row => row.id !== item.id))}><Trash size={18} /></IconButton>,
                },
              ]}
            />
          </Card>
        );
      })}

      {/* Footer */}
      <Typography level="body-xs" sx={{ color: 'text.tertiary', fontStyle: 'italic' }}>
        Quantities are estimates based on measured roof geometry. Verify counts on-site before ordering.
        {saved.data ? `Saved ${new Date(saved.data.updated_at).toLocaleString()}.` : 'Calculated from current measurement data.'}
      </Typography>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ position: 'sticky', bottom: 12, zIndex: 2, p: 2, borderRadius: 'md', bgcolor: 'background.surface', boxShadow: 'md', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box><Typography level="title-md">Ready to order materials?</Typography><Typography level="body-sm">Choose your distributor, confirm products, then review the order before sending.</Typography></Box>
        {orderButton}
      </Stack>
    </Stack>
  );
};
